export const baseScenarioState = Object.freeze({
  accuracyMode: "fast",
  frequency: 300,
  pulseWidth: 2,
  raytraceMode: "refracted",
  soundingSolutionMode: "measured",
  beamformingSvUsed: 1500,
  beamformingSvTrue: 1500,
  soundVelocity: 1500,
  svpMode: "constant",
  svSurfaceVelocity: 1505,
  svDeepVelocity: 1485,
  svTransitionDepth: 60,
  trueSoundVelocity: 1500,
  trueSvpMode: "constant",
  trueSvSurfaceVelocity: 1505,
  trueSvDeepVelocity: 1485,
  trueSvTransitionDepth: 60,
  range: 80,
  speed: 4,
  pingRate: 10,
  rawStripUpdateRate: 10,
  simTimeScale: 1,
  historyLength: 300,
  historyUnlimited: true,
  beamCount: 256,
  beamSpacingMode: "equiangular",
  beamAngle: 120,
  etopoGridSize: 36,
  terrainRevision: 0,
  rollEnabled: true,
  rollStabilizationEnabled: true,
  rollStabilizationStrength: 90,
  roll: 0,
  rollAmplitude: 6,
  rollPeriod: 8,
  rollVariability: 0.25,
  pitchEnabled: true,
  pitchStabilizationEnabled: true,
  pitchStabilizationStrength: 90,
  pitch: 0,
  pitchAmplitude: 2,
  pitchPeriod: 6,
  pitchVariability: 0.2,
  yawEnabled: true,
  yawStabilizationEnabled: true,
  yawStabilizationStrength: 90,
  yaw: 0,
  yawAmplitude: 3,
  yawPeriod: 10,
  yawVariability: 0.2,
  course: 0,
  steerage: 0,
  missionPlanEnabled: false,
  missionPlanHeading: 0,
  missionPlanOverlap: 20,
  missionOptimizeHeading: true,
  missionSkipTurns: false,
  missionWaypoints: [],
  cameraMode: "follow",
  missionLockAoi: true,
  verticalExaggeration: 1,
  seafloorPreset: "flat",
  wreckEnabled: false,
  wreckVisible: true,
  wreckScale: 1,
  wreckOffsetX: 260,
  wreckOffsetY: 0,
  wreckOffsetZ: 0,
  wreckYaw: 18,
  wreckRoll: 0,
  wreckPitch: 0,
  wreckEmbed: 4,
  wreckSonarTriangles: 300000,
  swathFillColor: "#ffa450",
  swathBeamsColor: "#ffcd8b",
  swathOutlineColor: "#000000",
  soundingPointsColor: "#ffffff",
  soundingColorMode: "depth",
  soundingColorRamp: "hydro",
  soundingColorInvert: false,
  soundingDepthMode: "auto",
  soundingDepthMin: 0,
  soundingDepthMax: 120,
  historySoundingsColor: "#ffffff",
  historyColorRamp: true,
  boatColor: "#e6f1fa",
  boatShape: "survey_launch",
  waterSurfaceColor: "#1a3f63",
  seafloorColor: "#4f6b55",
  meshGridColor: "#0ea5a3",
  swathFillOpacity: 0.35,
  swathBeamsOpacity: 1,
  swathOutlineOpacity: 0.9,
  soundingsOpacity: 1,
  historySoundingsOpacity: 0.6,
  boatOpacity: 1,
  waterSurfaceOpacity: 0.37,
  seafloorOpacity: 1,
  meshGridOpacity: 0.5,
  meshGridEnabled: true,
  meshGridSpacing: 4,
  densityTargetPrimary: 5,
  densityTargetSecondary: 10,
  densityQualityBasis: "touched",
  densityPaintingEnabled: false,
  gridNodesEnabled: false,
  gridSurfaceMode: "triangulated",
  gridSurfaceHillshade: true,
  gridHillshadeAzimuth: 315,
  gridHillshadeElevation: 45,
  gridSurfaceMaskNoData: true,
  gridNodeMetric: "mean",
  gridNodeColorRamp: "viridis",
  gridNodeOpacity: 0.8,
  gridNodeSize: 3,
  showSwathFill: true,
  showSwathBeams: true,
  showSwathOutline: false,
  showSoundings: true,
  showHistorySoundings: true,
  showBoat: true,
  showWaterSurface: false,
  showSeafloor: false,
  showOverlayGraphs: true,
  showOverlayMetrics: true,
  visualizationPreset: "clean_grid_daylight",
  backgroundColor: "#000000",
});

export function createScenarioState(overrides = {}) {
  return { ...baseScenarioState, ...overrides };
}

export function computeMetrics(state) {
  const effectiveSoundVelocity = getEffectiveSoundVelocity(state, state.range);
  const effectiveTrueSoundVelocity = getEffectiveSoundVelocity(state, state.range, "true");
  const twoWayTravelTime = (2 * state.range) / effectiveSoundVelocity;
  const rangeResolution = (effectiveSoundVelocity * (state.pulseWidth / 1000)) / 2;
  const swathWidth = 2 * state.range * Math.tan(degToRad(state.beamAngle / 2));
  const frequencyTradeoff = state.frequency > 400 ? "Higher detail, lower penetration" : "Higher penetration, lower detail";
  return {
    effectiveSoundVelocity,
    effectiveTrueSoundVelocity,
    twoWayTravelTime,
    rangeResolution,
    swathWidth,
    frequencyTradeoff,
  };
}

export function getEffectiveSoundVelocity(state, depth = 0, profile = "used") {
  const trueProfile = profile === "true";
  const mode = trueProfile ? state.trueSvpMode : state.svpMode;
  if (mode !== "layered") {
    return trueProfile ? state.trueSoundVelocity : state.soundVelocity;
  }
  const td = Math.max(1, trueProfile ? state.trueSvTransitionDepth : state.svTransitionDepth);
  const t = clamp(depth / td, 0, 1);
  const surface = trueProfile ? state.trueSvSurfaceVelocity : state.svSurfaceVelocity;
  const deep = trueProfile ? state.trueSvDeepVelocity : state.svDeepVelocity;
  return surface + (deep - surface) * t;
}

export function degToRad(value) {
  return (value * Math.PI) / 180;
}

export function beamIntensityProfile(state, t, sampleIndex, sampleCount) {
  const x = sampleCount <= 1 ? 0 : (sampleIndex / (sampleCount - 1)) * 2 - 1;
  const centerEnergy = Math.exp(-Math.pow(x * 1.8, 2));
  const edgeEnergy = Math.max(0, 1 - Math.pow(Math.abs(x), 1.35));
  const freqFactor = remap(state.frequency, 30, 700, 0.3, 1.0);
  const rangeLoss = remap(state.range, 10, 300, 1.0, 0.5);
  const pulseBlur = remap(state.pulseWidth, 0.1, 20, 0.25, 1.15);
  const rollStab = state.rollStabilizationEnabled ? clamp((state.rollStabilizationStrength ?? 100) / 100, 0, 1) : 0;
  const yawStab = state.yawStabilizationEnabled ? clamp((state.yawStabilizationStrength ?? 100) / 100, 0, 1) : 0;
  const rollOffset = state.rollEnabled === false ? 0 : state.roll * (1 - rollStab);
  const yawOffset = state.yawEnabled === false ? 0 : state.yaw * (1 - yawStab);
  const motion = Math.sin(t * 0.001 + x * 8 + degToRad(rollOffset) * 4) * 0.08;
  const yawRipple = Math.cos(t * 0.0006 + degToRad(yawOffset) * 2 + x * 4) * 0.06;
  return clamp((0.5 * centerEnergy + 0.5 * edgeEnergy) * freqFactor * rangeLoss * pulseBlur + motion + yawRipple, 0, 1);
}

function remap(value, inMin, inMax, outMin, outMax) {
  const n = clamp((value - inMin) / (inMax - inMin), 0, 1);
  return outMin + (outMax - outMin) * n;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
